import { useEffect, useMemo, useState } from "react";
import { localArtUrl } from "../art";
import { useGame, type LogEntry } from "../store";
import { OptionalImage } from "./OptionalImage";
import type { PointBreakdownItem } from "../types";

type Tab = "echoes" | "items" | "attrs" | "flags";
type EchoTone = "event" | "choice" | "win" | "loss" | "item" | "miss" | "luck";
type RelicTone = "help" | "harm" | "mixed";

interface EchoEntry {
  key: string;
  tone: EchoTone;
  label: string;
  title: string;
  detail: string;
}

interface EchoSummary {
  gained: number;
  missed: number;
  vowsKept: number;
  vowsBroken: number;
  luckDelta: number;
}

interface EchoBeat {
  id: number;
  event?: Extract<LogEntry, { kind: "event" }>;
  action?: Extract<LogEntry, { kind: "action" }>;
  result?: Extract<LogEntry, { kind: "result" }>;
  items: Extract<LogEntry, { kind: "items" }>["items"];
  challenges: Extract<LogEntry, { kind: "challenges" }>["items"];
  systems: string[];
}

interface ActiveRelic {
  itemId: string;
  itemName: string;
  stats: string[];
  statTones: Record<string, RelicTone>;
  tone: RelicTone;
  helpPoints: number;
  harmPoints: number;
}

function ItemFallback() {
  return (
    <div className="thumb item-fallback" aria-hidden="true">
      {Array.from({ length: 9 }, (_, i) => (
        <span key={i} />
      ))}
    </div>
  );
}

export function SidePanel() {
  const view = useGame((s) => s.view);
  const log = useGame((s) => s.log);
  const selectedAction = useGame((s) => s.selectedAction);
  const [tab, setTab] = useState<Tab>("echoes");
  const [tabTouched, setTabTouched] = useState(false);
  const activeBreakdown =
    view?.pending?.pointBreakdown ??
    (selectedAction?.needsCheck ? selectedAction.pointBreakdown : []);
  const activeStats = useMemo(() => {
    return new Set(activeBreakdown.map((it) => it.stat));
  }, [activeBreakdown]);
  const activeRelics = useMemo(
    () => activeRelicsFromBreakdown(activeBreakdown),
    [activeBreakdown],
  );
  const activeRelicStats = useMemo(
    () => new Set(activeRelics.flatMap((relic) => relic.stats)),
    [activeRelics],
  );
  const previewingCheck =
    !!selectedAction?.needsCheck ||
    (!!view?.pending?.needsCheck && !view.pending.rolled);
  const enteredResolvedBeat =
    !!view?.challenges.length ||
    !!view?.grantedItems.length;
  const activePointTotal = activeBreakdown.reduce((sum, item) => sum + item.points, 0);
  const echoes = useMemo(() => buildEchoes(log), [log]);
  const echoSummary = useMemo(() => buildEchoSummary(log), [log]);

  useEffect(() => {
    setTabTouched(false);
  }, [view?.event.id]);

  useEffect(() => {
    if (previewingCheck && !tabTouched) setTab("attrs");
  }, [previewingCheck, tabTouched]);

  useEffect(() => {
    if (!previewingCheck && enteredResolvedBeat && !tabTouched) setTab("echoes");
  }, [previewingCheck, enteredResolvedBeat, tabTouched, log.length]);

  if (!view) return null;
  const p = view.player;
  const chooseTab = (next: Tab) => {
    setTab(next);
    setTabTouched(true);
  };

  return (
    <aside className="side">
      <div className="luck-badge" title="气运：用于重掷命数的资源">
        气运 <b className="luck">⚜ {p.luck ?? 0}</b>
      </div>
      <div className="tabs">
        <button className={tab === "echoes" ? "on" : ""} onClick={() => chooseTab("echoes")}>
          <span className="tab-main">
            回响 {echoes.length > 0 && <span className="count">{echoes.length}</span>}
          </span>
        </button>
        <button className={tab === "items" ? "on" : ""} onClick={() => chooseTab("items")}>
          <span className="tab-main">
            藏品 {p.items.length > 0 && <span className="count">{p.items.length}</span>}
          </span>
        </button>
        <button className={tab === "attrs" ? "on" : ""} onClick={() => chooseTab("attrs")}>
          <span className="tab-main">{previewingCheck ? "本路修正" : "修正"}</span>
          {previewingCheck && activeBreakdown.length > 0 && (
            <small className="tab-hint">本次 {signed(activePointTotal)}</small>
          )}
        </button>
        <button className={tab === "flags" ? "on" : ""} onClick={() => chooseTab("flags")}>
          <span className="tab-main">线索</span>
        </button>
      </div>

      {tab === "echoes" && (
        <div className="panel-list echo-list">
          {echoes.length === 0 && <div className="empty">旅途尚未留下回响</div>}
          {echoes.length > 0 && (
            <div className="echo-summary">
              <span>得藏 {echoSummary.gained}</span>
              <span>错失 {echoSummary.missed}</span>
              <span>誓约 +{echoSummary.vowsKept}/-{echoSummary.vowsBroken}</span>
              <span>气运 {signed(echoSummary.luckDelta)}</span>
            </div>
          )}
          {echoes.map((echo) => (
            <div key={echo.key} className={`echo-row ${echo.tone}`}>
              <span className="echo-mark" aria-hidden="true" />
              <div>
                <div className="echo-label">{echo.label}</div>
                <div className="echo-title">{echo.title}</div>
                {echo.detail && <div className="echo-detail">{echo.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "items" && (
        <div className="panel-list">
          {p.items.length === 0 && <div className="empty">暂无藏品</div>}
          {p.items.map((it) => {
            const imageUrl = it.imageUrl ?? localArtUrl(`item:${it.id}`);
            const activeRelic = activeRelics.find((relic) => relic.itemId === it.id);
            return (
              <div
                key={it.id}
                className={`collectible ${activeRelic ? `echoing ${activeRelic.tone}` : ""}`}
                title={activeRelic ? relicToneTitle(activeRelic) : undefined}
              >
                  {activeRelic && (
                    <>
                      <span
                        className={`c-echo-mark ${activeRelic.tone}`}
                        aria-hidden="true"
                      />
                      <span className={`c-route-badge ${activeRelic.tone}`}>
                        {relicRouteLabel(activeRelic)}
                      </span>
                    </>
                  )}
                <OptionalImage
                  src={imageUrl}
                  alt=""
                  className="thumb"
                  loading="lazy"
                  fallback={<ItemFallback />}
                />
                <div>
                  <div className="c-name">{it.name}</div>
                  <div className="c-eff">{it.eff}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "attrs" && (
        <div className="panel-list">
          {previewingCheck && activeBreakdown.length > 0 && (
            <div className="attrs-context">
              这些修正正在影响本次检定，合计 {signed(activePointTotal)}
            </div>
          )}
          {Object.keys(p.adjustments).length === 0 && <div className="empty">暂无修正</div>}
          {Object.entries(p.adjustments).map(([k, v]) => {
            const relicTone = relicStatTone(activeRelics, k);
            const contribution = activeBreakdown.find((item) => item.stat === k)?.points;
            return (
              <div key={k} className={`stat-row ${activeStats.has(k) ? "active" : ""}`}>
                <span>{k}</span>
                {activeRelicStats.has(k) && relicTone && (
                  <em className={`stat-echo ${relicTone}`}>
                    {relicToneLabel(relicTone)}
                  </em>
                )}
                <b className={v >= 0 ? "pos" : "neg"}>
                  {contribution != null && activeStats.has(k) ? `本次 ${signed(contribution)}` : signed(v)}
                </b>
              </div>
            );
          })}
        </div>
      )}

      {tab === "flags" && (
        <div className="panel-list">
          {Object.keys(p.flags).length === 0 && <div className="empty">暂无线索</div>}
          {Object.entries(p.flags).map(([k, v]) => (
            <div key={k} className="stat-row">
              <span>{formatFlagName(k)}</span>
              <b>{v}</b>
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}

function buildEchoes(log: LogEntry[]): EchoEntry[] {
  const beats = buildBeats(log);
  return beats.slice(-6).map((beat, index, visibleBeats) => {
    const tone = beat.result
      ? beat.result.rolled.outcome >= 2
        ? "win"
        : "loss"
      : beat.challenges.some((challenge) => !challenge.passed)
        ? "loss"
        : beat.items.some((item) => item.gained)
          ? "item"
          : "event";
    const gained = beat.items.filter((item) => item.gained).map((item) => item.name);
    const missed = beat.items.filter((item) => !item.gained).map((item) => item.name);
    const kept = beat.challenges.filter((challenge) => challenge.passed).map((challenge) => challenge.name);
    const broken = beat.challenges.filter((challenge) => !challenge.passed).map((challenge) => challenge.name);
    const details = [
      beat.action ? `选择【${beat.action.name}】` : null,
      beat.result ? resultClause(beat.result) : null,
      kept.length ? `因此【${kept[0]}】应验` : null,
      broken.length ? `代价是【${broken[0]}】破裂` : null,
      gained.length ? `获得【${gained[0]}】` : null,
      missed.length ? `错失【${missed[0]}】` : null,
      beat.systems.find((text) => text.includes("气运"))?.replace(/[🎲✦]/g, "").trim() ?? null,
    ].filter(Boolean) as string[];
    const isLatest = index === visibleBeats.length - 1;
    return {
      key: `beat:${beat.id}`,
      tone,
      label: beat.result ? "因果回响" : isLatest ? "眼前局面" : "旧章余波",
      title: beat.event?.name ?? "未名一刻",
      detail: trimEcho(details.join("，") || beat.event?.des || "风声暂且安静。"),
    };
  });
}

function activeRelicsFromBreakdown(items: PointBreakdownItem[]): ActiveRelic[] {
  const byId = new Map<string, ActiveRelic>();
  for (const item of items) {
    for (const contribution of relicContributions(item)) {
      if (contribution.points === 0) continue;
      const tone: RelicTone = contribution.points > 0 ? "help" : "harm";
      const existing = byId.get(contribution.itemId);
      if (existing) {
        if (!existing.stats.includes(item.stat)) existing.stats.push(item.stat);
        existing.statTones[item.stat] = mergeRelicTone(existing.statTones[item.stat], tone);
        existing.tone = mergeRelicTone(existing.tone, tone);
        if (contribution.points > 0) existing.helpPoints += contribution.points;
        if (contribution.points < 0) existing.harmPoints += contribution.points;
      } else {
        byId.set(contribution.itemId, {
          itemId: contribution.itemId,
          itemName: contribution.itemName,
          stats: [item.stat],
          statTones: { [item.stat]: tone },
          tone,
          helpPoints: Math.max(0, contribution.points),
          harmPoints: Math.min(0, contribution.points),
        });
      }
    }
  }
  return [...byId.values()];
}

function relicContributions(item: PointBreakdownItem): Array<{
  itemId: string;
  itemName: string;
  points: number;
}> {
  if (item.sourceContributions?.length) {
    return item.sourceContributions.map((source) => ({
      itemId: source.sourceItemId,
      itemName: source.sourceLabel,
      points: source.points,
    }));
  }
  if (!item.sourceItemIds?.length || item.points === 0 || item.sourceItemIds.length !== 1) return [];
  return [{
    itemId: item.sourceItemIds[0],
    itemName: item.sourceLabels?.[0] ?? "旧物",
    points: item.points,
  }];
}

function mergeRelicTone(a: RelicTone | undefined, b: RelicTone): RelicTone {
  if (!a || a === b) return b;
  return "mixed";
}

function relicStatTone(relics: ActiveRelic[], stat: string): RelicTone | null {
  return relics.reduce<RelicTone | null>((tone, relic) => {
    const statTone = relic.statTones[stat];
    if (!statTone) return tone;
    return tone ? mergeRelicTone(tone, statTone) : statTone;
  }, null);
}

function relicToneLabel(tone: RelicTone): string {
  if (tone === "help") return "旧物应验";
  if (tone === "harm") return "旧物牵累";
  return "旧物两面";
}

function relicRouteLabel(relic: ActiveRelic): string {
  if (relic.tone === "mixed") return `两面 ${signed(relic.helpPoints)}/${signed(relic.harmPoints)}`;
  return `本路 ${signed(relic.helpPoints || relic.harmPoints)}`;
}

function relicToneTitle(relic: ActiveRelic): string {
  if (relic.tone === "mixed") return `此物两面牵动本路：${signed(relic.helpPoints)} / ${signed(relic.harmPoints)}`;
  if (relic.tone === "harm") return `此物正在牵累本路：${signed(relic.harmPoints)}`;
  return `此物正在呼应本路：${signed(relic.helpPoints)}`;
}

function buildBeats(log: LogEntry[]): EchoBeat[] {
  const beats: EchoBeat[] = [];
  let current: EchoBeat | null = null;
  log.forEach((entry) => {
    switch (entry.kind) {
      case "event":
        if (current) beats.push(current);
        current = { id: entry.id, event: entry, items: [], challenges: [], systems: [] };
        break;
      case "action":
        current = ensureBeat(current, entry.id);
        current.action = entry;
        break;
      case "result":
        current = ensureBeat(current, entry.id);
        current.result = entry;
        break;
      case "challenges":
        current = ensureBeat(current, entry.id);
        current.challenges.push(...entry.items);
        break;
      case "items":
        current = ensureBeat(current, entry.id);
        current.items.push(...entry.items);
        break;
      case "system":
        current = ensureBeat(current, entry.id);
        current.systems.push(entry.text);
        break;
    }
  });
  if (current) beats.push(current);
  return beats.filter((beat) => beat.event || beat.action || beat.result || beat.items.length || beat.challenges.length || beat.systems.length);
}

function ensureBeat(current: EchoBeat | null, id: number): EchoBeat {
  return current ?? { id, items: [], challenges: [], systems: [] };
}

function buildEchoSummary(log: LogEntry[]): EchoSummary {
  return log.reduce<EchoSummary>(
    (summary, entry) => {
      if (entry.kind === "items") {
        summary.gained += entry.items.filter((item) => item.gained).length;
        summary.missed += entry.items.filter((item) => !item.gained).length;
      }
      if (entry.kind === "challenges") {
        summary.vowsKept += entry.items.filter((challenge) => challenge.passed).length;
        summary.vowsBroken += entry.items.filter((challenge) => !challenge.passed).length;
      }
      if (entry.kind === "system") summary.luckDelta += parseLuckDelta(entry.text);
      return summary;
    },
    { gained: 0, missed: 0, vowsKept: 0, vowsBroken: 0, luckDelta: 0 },
  );
}

function resultClause(entry: Extract<LogEntry, { kind: "result" }>): string {
  if (entry.rolled.outcome === 3) return "大成功，因此气运回流";
  if (entry.rolled.outcome === 2) return `检定越线 ${entry.rolled.total}/${entry.req}`;
  if (entry.rolled.outcome === 1) return `检定受挫 ${entry.rolled.total}/${entry.req}`;
  return "大失败，命线崩裂";
}

function parseLuckDelta(text: string): number {
  const spend = text.match(/-\s*(\d+)\s*气运/);
  if (spend) return -Number(spend[1]);
  const gain = text.match(/气运\s*\+(\d+)/);
  if (gain) return Number(gain[1]);
  return 0;
}

function trimEcho(text: string): string {
  const compact = text.replace(/\s+/g, " ").trim();
  return compact.length > 54 ? `${compact.slice(0, 54)}...` : compact;
}

function formatFlagName(key: string): string {
  const known: Record<string, string> = {
    rain_oath_kept: "雨誓已守",
    rain_oath_fractured: "雨誓破裂",
  };
  if (known[key]) return known[key];
  const cleaned = key
    .replace(/^flag[:_-]?/i, "")
    .replace(/[:_-]+/g, " ")
    .trim();
  return cleaned ? `印记：${cleaned}` : "无名印记";
}

function signed(n: number): string {
  return n >= 0 ? `+${n}` : String(n);
}
